import Dropzone, { type DropzoneRef } from "@/components/ui/baseUi/dropzone"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { cn } from "@/modules/lib/utils"
import { useUploadMedia } from "@/modules/ui/components/fileUpload/hooks/useUploadMedia"
import { word } from "@modules/core/utils/i18n"
import { IMAGE_MAX_SIZE, readFile, resizeImage } from "@modules/ui/components/fileUpload/fileUploadUtils"
import { type RefObject } from "react"

type FileUploadImageProps = {
  setImage: (i?: MediaItemFormType) => void
  onStartUpload?: () => void
  onEndUpload?: () => void
  disabled?: boolean
  acceptedTypes?: string
  ref?: RefObject<DropzoneRef>
} & React.ComponentProps<"div">

export function FileUploadImage({
  setImage,
  onStartUpload,
  onEndUpload,
  className,
  disabled,
  children,
  acceptedTypes,
  ref,
  ...props
}: FileUploadImageProps) {
  const { uploadMedia, isPending } = useUploadMedia()

  const onDropFiles = async (files: File[]) => {
    try {
      onStartUpload?.()
      const newImage = files[0]
      if (!newImage) return
      const resizedImage = await resizeImage(newImage)

      if (resizedImage.size > IMAGE_MAX_SIZE) {
        toast.error(word("global.error.label"), {
          description: word("file_upload.max_size.error"),
          classNames: errorToastClassName(),
        })
        onEndUpload?.()
        return
      }

      const result = await readFile(resizedImage)

      if (result) {
        uploadMedia(newImage)
          .then((response) => {
            setImage({
              url: "",
              file: newImage,
              key: response.awsS3Key ?? null,
              type: MediaType.S3_IMAGE,
              filename: newImage.name,
              isUploading: true,
            })
          })
          .finally(() => {
            onEndUpload?.()
          })
      } else {
        toast.error(word("global.error.label"), {
          description: word("global.fileUpload.error"),
          classNames: errorToastClassName(),
        })

        onEndUpload?.()
      }
    } catch (e: unknown) {
      onEndUpload?.()

      if (e instanceof Error) {
        console.error(`[FileUpload] ${e.message}`)
      } else {
        console.error(`[FileUpload] ${String(e)}`)
      }
      toast.error(word("global.error.label"), {
        description: word("global.fileUpload.error"),
        classNames: errorToastClassName(),
      })
    }
  }

  return (
    <Dropzone
      ref={ref}
      onFilesAdded={(files) => void onDropFiles(files)}
      className={cn(
        "group/dropzone w-full transition-opacity",
        (disabled || isPending) && "cursor-default",
        !disabled && !isPending && "**:cursor-pointer hover:opacity-60",
        className,
      )}
      data-loading={isPending}
      disabled={disabled || isPending}
      accept={acceptedTypes}
      {...props}
    >
      {children}
    </Dropzone>
  )
}
